import dayjs from "dayjs";
import { isEquitySellType } from "../../../utils/reports/equityTransactionTypes";
import { isMfSellTx } from "../../../utils/mf/mfTransactionTypes";
import {
  formatReportTypeColumnLabel,
  resolveReportSaleAmount,
} from "../../../utils/reports/reportDisplayLabels";
import { withTxnBenchmarkFields } from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const resolveCurrentDateOrToday = (row: Record<string, any>): string =>
  formatEpochDate(row.currentDate ?? row.current_date ?? Date.now());

const getHoldingSchemeName = (row: Record<string, any>): string =>
  String(row.symbol ?? row.schemeName ?? row.name ?? "-");

const getHoldingTransactionList = (holding: Record<string, any>): any[] => {
  for (const key of [
    "transactions",
    "txns",
    "transactionDetails",
    "transactionList",
    "transaction",
    "trxn",
  ] as const) {
    const list = holding[key];
    if (Array.isArray(list) && list.length > 0) return list;
  }
  return [];
};

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ??
      row.txnType ??
      row.transactionType ??
      row.transactiontype ??
      "",
  ).trim();
  if (raw) {
    if (isEquitySellType(raw) || isMfSellTx(raw)) return true;
    const txnType = raw.toLowerCase();
    if (
      txnType.includes("sell") ||
      txnType.includes("redemption") ||
      txnType.includes("redeem") ||
      txnType === "sale"
    ) {
      return true;
    }
    return false;
  }
  return row.avgBuyPrice != null && row.avgBuyPrice !== "" && row.avgBuyPrice !== "-";
};

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === "" || v === "-") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

const filterPositiveUnits = (rows: any[]) =>
  rows.filter((row) => {
    if (!row || typeof row !== "object") return true;
    const units = toNum(row.units ?? row.quantity ?? row.qty);
    if (units === null) return true;
    if (units > 0) return true;
    const invested = toNum(row.investedAmount ?? row.costValue ?? row.amount);
    const current = toNum(row.currentAmount ?? row.currentValue ?? row.marketValue);
    return (invested !== null && invested > 0) || (current !== null && current > 0);
  });

const filterRealizedTxnRows = (rows: any[]) =>
  rows.filter((r) => isSellTxn(r)).map((r) => ({
    ...r,
    type: formatReportTypeColumnLabel("Realized", "Realized"),
    displayTransType: "Realized",
  }));

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const val =
    row.averageDays ??
    row.averageDay ??
    row.days ??
    meta.averageDays ??
    meta.days;
  return val != null && val !== "" ? val : "-";
};

const resolveTxnDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const val = row.days ?? meta.days;
  return val != null && val !== "" ? val : "-";
};

/**
 * Holdings API weighted CAGR for remaining lots is 10× smaller than aumsampaan
 * Unrealized / All display (14.85 → 148.50, portfolio 13.71 → 137.07).
 * Sell-lot CAGR is already a full percent and must not be scaled.
 */
const scaleIntlEquityHoldingCagr = (value: unknown): string | number => {
  const n = toNum(value);
  if (n === null) return value != null && value !== "" ? (value as string | number) : "-";
  if (n === 0) return 0;
  return n * 10;
};

const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrIntlEquity"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return scaleIntlEquityHoldingCagr(val);
  }
  return row.cagr != null && row.cagr !== ""
    ? scaleIntlEquityHoldingCagr(row.cagr)
    : "-";
};

/** All / Realized sell rows: txn CAGR only — do not fall back to holding CAGR. */
const resolveTxnCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  const val = row.cagr ?? meta.cagr;
  return val != null && val !== "" ? val : "-";
};

const normalizeUnrealizedHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      symbol: row.symbol ?? schemeName,
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      units: row.units ?? row.quantity,
      amount: row.amount ?? row.investedAmount ?? row.costValue,
      currentAmount: row.currentAmount ?? row.currentValue ?? row.marketValue,
      averagePrice:
        row.averagePrice ?? row.avgPrice ?? row.averageNav ?? meta.averagePrice,
      currentPrice:
        row.currentPrice ?? row.currentNAV ?? row.currentNav ?? meta.currentPrice,
      gainLoss:
        row.unrealisedPNL ??
        row.unrealisedPnL ??
        row.unrealizedPNL ??
        row.unrealisedGainLoss ??
        row.gainLoss,
      gainLossPerc:
        row.unrealisedPNLPercentage ??
        row.unrealisedPnLPercentage ??
        row.unrealizedPNLPercentage ??
        row.gainLossPercentage ??
        row.gainLossPerc,
      days: resolveHoldingAverageDays(row),
      cagr: resolveHoldingCagr(row),
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
    }),
  );
};

const normalizeRealizedRow = (row: Record<string, any>) => {
  const meta = row.metadata ?? row.metaData ?? row.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  const avgBuy =
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.avgPurchasePrice ??
    row.averagePrice ??
    row.avgPrice ??
    meta.avgBuyPrice ??
    meta.averageBuyPrice ??
    meta.averagePrice ??
    "-";
  const purchaseNAV =
    row.purchaseNAV ??
    row.purchaseNav ??
    row.purchasePrice ??
    row.nav ??
    row.price ??
    meta.purchaseNAV ??
    meta.purchasePrice;
  const saleValue = resolveReportSaleAmount(row) ?? row.saleValue ?? row.amount;
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      symbol: row.symbol ?? schemeName,
      type: formatReportTypeColumnLabel("Realized", "Realized"),
      displayTransType: "Realized",
      displayTransactionType: "Realized",
      transactionDate: formatEpochDate(
        row.transactionDate ?? row.transactiondate ?? row.date,
      ),
      purchaseNAV: purchaseNAV ?? "-",
      avgBuyPrice: avgBuy,
      saleValue,
      units: row.units ?? row.quantity ?? row.qty,
      realisedPnl:
        row.realisedPnl ??
        row.realisedPnL ??
        row.realisedPNL ??
        row.realizedPnl ??
        row.realisedGainLoss,
      realisedPNLPercentage:
        row.realisedPNLPercentage ??
        row.realisedPnLPercentage ??
        row.realizedPNLPercentage,
      cagr: resolveTxnCagr(row),
      days: resolveTxnDays(row),
      gainLoss: undefined,
    }),
  );
};

const flattenIntlEquityHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const schemeName = getHoldingSchemeName(h);
    const list = getHoldingTransactionList(h);
    if (!list.length) {
      if (h.transType || h.txnType || h.transactionType) {
        txns.push(h);
      }
      continue;
    }
    for (const t of list) {
      const transType = t.transType ?? t.txnType ?? t.transactionType;
      txns.push(
        withTxnMetadataFields(
          withTxnBenchmarkFields({
            ...t,
            schemeName: t.schemeName ?? t.symbol ?? schemeName,
            symbol: t.symbol ?? h.symbol ?? schemeName,
            userId: t.userId ?? h.userId,
            memberName: t.memberName ?? h.memberName,
            transactionDate: t.transactionDate ?? t.transactiondate ?? t.date,
            transType,
            txnType: t.txnType ?? transType,
            transactionType: t.transactionType ?? transType,
            purchasePrice:
              t.purchasePrice ?? t.price ?? t.avgPrice ?? t.averagePrice,
            purchaseNAV:
              t.purchaseNAV ?? t.purchaseNav ?? t.purchasePrice ?? t.price,
            currentPrice: t.currentPrice ?? t.currentNAV ?? h.currentPrice,
            amount: t.amount ?? t.costValue ?? t.investedAmount,
            currentAmount: t.currentAmount ?? t.currentValue,
            units: t.units ?? t.quantity ?? t.qty,
            days: resolveTxnDays(t),
            avgBuyPrice: t.avgBuyPrice ?? t.averageBuyPrice,
            cagr: resolveTxnCagr(t),
            realisedPnl:
              t.realisedPnl ??
              t.realisedPnL ??
              t.realisedPNL ??
              t.realizedPnl ??
              t.realizedPNL,
            realisedPNLPercentage:
              t.realisedPNLPercentage ??
              t.realisedPnLPercentage ??
              t.realizedPNLPercentage ??
              t.realizedPnLPercentage,
          }),
        ),
      );
    }
  }
  return txns;
};

const normalizeHoldingRowForAll = (row: Record<string, any>) => {
  const normalized = normalizeUnrealizedHoldingRow({
    ...row,
    type: "Unrealized",
  });
  return {
    ...normalized,
    costValue: normalized.amount ?? normalized.costValue ?? normalized.investedAmount,
    currentValue: normalized.currentAmount ?? normalized.currentValue,
    gainLoss: normalized.gainLoss,
    gainLossPercentage: normalized.gainLossPerc ?? normalized.gainLossPercentage,
  };
};

const normalizeTxnRowForAll = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const schemeName = getHoldingSchemeName(row);
  const normalized = normalizeRealizedRow({ ...row, schemeName });
  const pnl =
    row.realisedPnl ??
    row.realisedPnL ??
    row.realisedPNL ??
    row.realizedPnl ??
    row.realizedPNL ??
    row.realisedGainLoss ??
    normalized.realisedPnl;
  const pnlPct =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage ??
    row.realizedPnLPercentage ??
    normalized.realisedPNLPercentage;
  return {
    ...normalized,
    schemeName,
    type: formatReportTypeColumnLabel(
      isSell ? "Realized" : "Unrealized",
      isSell ? "Realized" : "Unrealized",
    ),
    costValue: isSell ? "-" : (row.amount ?? row.costValue),
    currentValue: isSell ? "-" : (row.currentAmount ?? row.currentValue),
    days: resolveTxnDays(row),
    cagr: resolveTxnCagr(row),
    gainLoss: pnl ?? "-",
    gainLossPercentage: pnlPct ?? "-",
  };
};

const extractIntlEquityHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data?.data?.holdings)) return payload.data.data.holdings;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null => {
  const info =
    payload?.totalHoldingInfo ??
    payload?.totalHolding ??
    payload?.data?.totalHoldingInfo ??
    null;
  if (!info || typeof info !== "object" || Object.keys(info).length === 0) {
    return null;
  }
  return {
    ...info,
    totalValueInvested:
      info.totalValueInvested ?? info.totalInvested ?? info.investedAmount,
    totalInvested:
      info.totalInvested ?? info.totalValueInvested ?? info.investedAmount,
    currentValue: info.currentValue ?? info.currentAmount,
    unrealisedGainLoss: info.unrealisedGainLoss ?? info.gainLoss,
    gainLoss: info.gainLoss ?? info.unrealisedGainLoss,
    gainLossPercentage: info.gainLossPercentage ?? info.gainLossPerc,
    roi: info.roi,
    cagr: scaleIntlEquityHoldingCagr(info.cagr),
    ...(info.cagrIntlEquity != null
      ? { cagrIntlEquity: scaleIntlEquityHoldingCagr(info.cagrIntlEquity) }
      : {}),
  };
};

export type ParsedIntlEquityReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
};

export const parseIntlEquityReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedIntlEquityReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;

  switch (reportType) {
    case "Holding": {
      const holdings = extractIntlEquityHoldings(payload);
      rows = filterPositiveUnits(holdings).map(normalizeUnrealizedHoldingRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) ?? rows.length;
      break;
    }
    case "Realized": {
      const holdings = extractIntlEquityHoldings(payload);
      rows = filterRealizedTxnRows(
        flattenIntlEquityHoldingsTransactions(holdings),
      ).map(normalizeRealizedRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "All": {
      const holdings = extractIntlEquityHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      const unrealized = filterPositiveUnits(holdings).map(normalizeHoldingRowForAll);
      const realized = filterRealizedTxnRows(
        flattenIntlEquityHoldingsTransactions(holdings),
      ).map(normalizeTxnRowForAll);
      rows = [...unrealized, ...realized];
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "Transaction": {
      const holdings = extractIntlEquityHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      rows = flattenIntlEquityHoldingsTransactions(holdings);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
  };
};

export const mergeIntlEquityTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.symbol ?? r.schemeName}-${r.transactionDate}-${r.units}-${r.transType}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.symbol ?? row.schemeName}-${row.transactionDate}-${row.units}-${row.transType}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
};
